import type { Observable } from 'rxjs';
import { type ELanguages } from '@common/enums';
import type {
    LibraryBlockApiEntity,
    LibraryBlockEntity,
    UnsavedLibraryBlockEntity,
} from '@web/store/library-blocks/library-blocks.models';

import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';

import { BaseHttpService } from '@common/services/REST/base.http.service';

type PaginationResponse<T = object> = {
    current_page: number;
    data: T;
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    next_page_url: string;
    path: string;
    per_page: number;
    prev_page_url: string;
    to: number;
    total: number;
};

@Injectable({
    providedIn: 'root',
})
export class LibraryBlocksHttpService extends BaseHttpService {
    private apiUrl = (isAdmin) => this.formatApiUrl('blocks', isAdmin ? '/admin' : '');

    public getLibraryBlocks(templateId): Observable<LibraryBlockApiEntity[]> {
        const url = '/admin' + this.formatApiUrl(`templates/${templateId}/blocks`);

        return this.http.get<LibraryBlockApiEntity[]>(url);
    }

    public getUserLibraryBlocks(lang: ELanguages): Observable<LibraryBlockApiEntity[]> {
        return this.http
            .get<PaginationResponse<LibraryBlockApiEntity[]>>(this.formatApiUrl(`blocks?lang=${lang}`))
            .pipe(map((r) => r.data));
    }

    public createLibraryBlock(
        libraryBlock: UnsavedLibraryBlockEntity,
        isAdmin?: boolean
    ): Observable<LibraryBlockApiEntity> {
        return this.http.post<LibraryBlockApiEntity>(this.apiUrl(isAdmin), libraryBlock);
    }

    public updateLibraryBlock(libraryBlock: LibraryBlockEntity): Observable<LibraryBlockApiEntity> {
        const isAdmin = true;
        return this.http.put<LibraryBlockApiEntity>(`${this.apiUrl(isAdmin)}/${libraryBlock.id}`, libraryBlock);
    }

    public deleteLibraryBlock(libraryBlock: LibraryBlockEntity, isAdmin?: boolean): Observable<LibraryBlockApiEntity> {
        return this.http.delete<LibraryBlockApiEntity>(`${this.apiUrl(isAdmin)}/${libraryBlock.id}`);
    }
}
